// Pure helpers for the ⌘K palette (no React): the "reads like a sentence" check that shows the
// Ask-AI row, and defensive parsers for the catalog/orders search endpoints (shapes owned by
// other modules; anything unexpected is dropped rather than rendered).

/** "Does this read like a sentence for the AI?" — multi-word questions/commands, not lookups like "#1001" or a SKU. */
export function looksLikeSentence(q: string): boolean {
  const t = q.trim()
  if (t.length < 8) return false
  const words = t.split(/\s+/).filter(Boolean)
  if (/[?]$/.test(t) && words.length >= 2) return true
  if (/^(how|what|why|when|where|which|who|can|could|should|show|find|list|create|make|add|set|change|turn|enable|disable|write|draft|give|help|tell|update|remove|delete|increase|decrease|compare)\b/i.test(t) && words.length >= 3) return true
  return words.length >= 4 && /[a-z]{3,}/i.test(t)
}

export interface Hit { id: string; kind: 'product' | 'collection' | 'order' | 'customer'; title: string; subtitle?: string; to: string; image?: string | null; status?: string | null; amount?: number; currency?: string }

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : undefined)
/** Rows of a list field: anything that isn't a plain object (null, strings, numbers, arrays) is dropped. */
const arr = (v: unknown): any[] => (Array.isArray(v) ? v.filter(x => x !== null && typeof x === 'object' && !Array.isArray(x)) : [])

export function parseCatalog(data: unknown): Hit[] {
  if (!data || typeof data !== 'object') return []
  const d = data as Record<string, unknown>
  const products = arr(d.products).slice(0, 5).map(p => ({
    id: str(p.id) ?? '', kind: 'product' as const, title: str(p.title) ?? 'Untitled product', subtitle: str(p.sku) ?? str(p.vendor),
    to: `/products/${encodeURIComponent(str(p.id) ?? '')}`, image: str(p.imageUrl) ?? str(p.image?.url) ?? str(p.thumbnail) ?? null, status: str(p.status) ?? null,
  })).filter(h => !!h.id)
  const collections = arr(d.collections).slice(0, 3).map(c => ({
    id: str(c.id) ?? '', kind: 'collection' as const, title: str(c.title) ?? 'Untitled collection', subtitle: typeof c.productsCount === 'number' ? `${c.productsCount} products` : undefined,
    to: `/collections/${encodeURIComponent(str(c.id) ?? '')}`,
  })).filter(h => !!h.id)
  return [...products, ...collections]
}
export function parseOrders(data: unknown): Hit[] {
  if (!data || typeof data !== 'object') return []
  const d = data as Record<string, unknown>
  const orders = arr(d.orders).slice(0, 5).map(o => ({
    id: str(o.id) ?? '', kind: 'order' as const, title: str(o.name) ?? `#${str(o.number) ?? ''}`,
    subtitle: str(o.customerName) ?? str(o.customer?.name) ?? str(o.email) ?? str(o.phone),
    to: `/orders/${encodeURIComponent(str(o.id) ?? '')}`, status: str(o.financialStatus) ?? null,
    amount: typeof o.total === 'number' ? o.total : undefined, currency: str(o.currency),
  })).filter(h => !!h.id)
  const customers = arr(d.customers).slice(0, 5).map(c => {
    const name = str(c.name) ?? ([str(c.firstName), str(c.lastName)].filter(Boolean).join(' ') || undefined)
    return { id: str(c.id) ?? '', kind: 'customer' as const, title: name ?? str(c.email) ?? str(c.phone) ?? 'Customer', subtitle: name ? (str(c.email) ?? str(c.phone)) : undefined, to: `/customers/${encodeURIComponent(str(c.id) ?? '')}` }
  }).filter(h => !!h.id)
  return [...orders, ...customers]
}

